import { useContext } from 'react';
import AuthContext, { User } from '../context/AuthContext';

interface UseAuthReturn {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSupervisor: boolean;
  login: (token: string, remember: boolean) => void;
  logout: () => void;
  hasRole: (roles: string[]) => boolean;
  canDelete: boolean;
  canEdit: boolean;
  canCreate: boolean;
  canView: boolean;
}

const useAuth = (): UseAuthReturn => {
  const { user, isLoading, login, logout } = useContext(AuthContext);
  
  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';
  
  /**
   * Check if user has one of the specified roles
   */
  const hasRole = (roles: string[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };
  
  // Permission helpers based on role-based access control
  const canDelete = isAdmin || isSupervisor; // Admin and supervisor can delete
  const canEdit = isAuthenticated; // All authenticated users can edit
  const canCreate = isAuthenticated; // All authenticated users can create
  const canView = isAuthenticated; // All authenticated users can view
  
  return {
    user,
    isLoading,
    isAuthenticated,
    isAdmin,
    isSupervisor,
    login,
    logout,
    hasRole,
    canDelete,
    canEdit,
    canCreate,
    canView
  };
};

export default useAuth; 